import { useContext, useRef, useState } from 'react'
import { Form } from 'react-router'
import { AddCircleOutlineOutlined, CheckOutlined, EmailOutlined, MobileFriendly, PersonOutlined, PhoneOutlined } from '@mui/icons-material'
import { Avatar, Box, Button, FormHelperText, Grid, Typography } from '@mui/material'
import { Formik, FormikProps } from 'formik'
import { useSnackbar } from 'notistack'
import * as Yup from 'yup'

import { AuthenticationContext } from '../../providers/auth-provider'
import useStyles from '../../routes/contacts/index.styles'
import Api from '../../utils/api'
import { formatIntlPhoneNumber, formatPhoneNumber } from '../../utils/helpers'
import useSharedStyles from '../../utils/shared.styles'
import FormikTextField from '../formik-input'
import FullScreenModal from '../full-screen-modal'
import BasicDialog from '../m3/basic-dialog'
import ButtonGroup from '../m3/button-group'

import contactDialogStyles from './index.styles'

interface FormValues {
  firstName: string
  lastName: string
  email: string
  mainNumber: string
  mainNumberType: PhoneType
  backupNumber: string
  backupNumberType: PhoneType
  preferences: string[]
  role: string
}

interface IProps {
  selectedFolderId: number
  openContactForm: boolean
  handleCloseContactForm: () => void
  reload?: () => Promise<void>
  allContacts?: INotificationContact[]
  editUser?: IUserType
}

let alreadyValidatedMainNumber: string
let currentlyValidatingMainNumber: string | undefined
let isEnteringMainNumber = false
let alreadyValidatedBackupNumber: string
let currentlyValidatingBackupNumber: string | undefined
let isEnteringBackupNumber = false

const ContactDialog = (props: IProps) => {
  const { selectedFolderId, openContactForm, handleCloseContactForm, reload, editUser, allContacts } = props

  const classes = useStyles
  const sharedClasses = useSharedStyles
  const { enqueueSnackbar } = useSnackbar()

  const { ownUser, setOwnUser } = useContext(AuthenticationContext)
  const [showDiscardDialog, setShowDiscardDialog] = useState(false)
  const [showBackupPhone, setShowBackupPhone] = useState(editUser && !!editUser.metadata?.backup_phone)
  const editingSelf = editUser && editUser?.id === ownUser?.id

  const isDirtyRef = useRef(false)
  const formikRef = useRef<FormikProps<FormValues>>(null)

  const handleClose = () => {
    if (isDirtyRef.current) {
      setShowDiscardDialog(true)
    } else {
      handleCloseContactForm()
      setShowBackupPhone(false)
    }
  }

  const handleDiscardChanges = () => {
    setShowDiscardDialog(false)
    formikRef.current?.resetForm()
    handleCloseContactForm()
    setShowBackupPhone(false)
  }

  const schema = Yup.object().shape({
    firstName: Yup.string().test('requiredFirstName', 'Required', function requiredFirstNameTest() {
      return this.parent.firstName?.trim()
    }),
    lastName: Yup.string().test('requiredLastName', 'Required', function requiredLastNameTest() {
      return this.parent.role === 'Call Center' || (this.parent.role !== 'Call Center' && this.parent.lastName?.trim())
    }),
    email: Yup.string().trim().required('Required').email('Please enter correct email format.'),
    mainNumber: Yup.string()
      .trim()
      .required('Required')
      .test('validMainNumber', 'Please enter a valid phone number.', function validMainNumber() {
        const enteredPhoneNumber = `${this.parent.mainNumber}`
        const phoneNumber = enteredPhoneNumber.replace(/[^+\d]/g, '') // Remove everything except digits and optional + to avoid the endpoint's max character limit of 15
        const requiredMainNumberType: PhoneType = this.parent.role === 'Call Center' ? 'Landline' : 'Mobile Phone'
        const expectedMainNumberTypeError =
          requiredMainNumberType === 'Landline' ? 'Please enter a valid landline number.' : 'Please enter a valid mobile number.'

        // To limit the number of API calls, we only check that this is a valid mobile number via the API if it hasn't already been checked and if the user has left the field
        if (alreadyValidatedMainNumber !== phoneNumber && currentlyValidatingMainNumber !== phoneNumber && !isEnteringMainNumber) {
          currentlyValidatingMainNumber = phoneNumber
          return new Promise((resolve) => {
            Api.get(`/api/users/phoneinfo?number=${phoneNumber}`)
              .then((res) => {
                const carrierType = res.body?.carrier?.type
                const hasCarrierError = !!res.body?.carrier?.error_code
                const isMobileNumber = res.ok && !hasCarrierError && carrierType === 'mobile'
                const isLandlineNumber = res.ok && !hasCarrierError && carrierType === 'landline'

                if ((isMobileNumber && requiredMainNumberType === 'Mobile Phone') || (isLandlineNumber && requiredMainNumberType === 'Landline')) {
                  alreadyValidatedMainNumber = phoneNumber
                  resolve(true)
                } else if (res.status === 404 && !enteredPhoneNumber.includes('+') && enteredPhoneNumber.replace(/\D/g, '').length !== 9) {
                  resolve(this.createError({ message: 'Please include country code.' }))
                } else {
                  resolve(this.createError({ message: expectedMainNumberTypeError }))
                }
              })
              .catch(() => {
                resolve(this.createError({ message: expectedMainNumberTypeError }))
              })
              .finally(() => {
                currentlyValidatingMainNumber = undefined
              })
          })
        }
        return true
      }),
    backupNumber: Yup.string().test('validBackupNumber', 'Please enter a valid phone number.', function validBackupNumber() {
      if (!this.parent.backupNumber) return true

      const enteredBackupNumber = this.parent.backupNumber as string
      const phoneNumber = enteredBackupNumber.replace(/-|\s/g, '') // Remove spaces and hyphens to avoid the endpoint's max character limit of 15

      // To limit the number of API calls, we only check that this is a valid phone number via the API if it hasn't already been checked and if the user has left the field
      if (alreadyValidatedBackupNumber !== phoneNumber && currentlyValidatingBackupNumber !== phoneNumber && !isEnteringBackupNumber) {
        currentlyValidatingBackupNumber = phoneNumber
        return new Promise((resolve) => {
          Api.get(`/api/users/phoneinfo?number=${phoneNumber}`)
            .then((res) => {
              if (res.status === 404 && !enteredBackupNumber.includes('+') && enteredBackupNumber.replace(/\D/g, '').length !== 9) {
                resolve(this.createError({ message: 'Please include country code.' }))
              } else if (res.ok && !res.body?.carrier?.error_code) {
                alreadyValidatedBackupNumber = phoneNumber
                resolve(true)
              } else resolve(false)
            })
            .catch(() => {
              resolve(false)
            })
            .finally(() => {
              currentlyValidatingBackupNumber = undefined
            })
        })
      }
      return true
    }),
  })

  const initialValues: FormValues = {
    firstName: editUser?.information.first || '',
    lastName: editUser?.information.last || '',
    email: editUser?.email || '',
    mainNumber: editUser?.phone ? formatPhoneNumber(editUser.phone) : '',
    mainNumberType: editUser?.metadata?.phone_type || (editUser?.role?.name === 'Call Center' ? 'Landline' : 'Mobile Phone'),
    backupNumber: editUser?.metadata?.backup_phone ? formatPhoneNumber(editUser.metadata.backup_phone) : '',
    backupNumberType: editUser?.metadata?.backup_phone_type || (editUser?.role?.name === 'Call Center' ? 'Landline' : ''),
    preferences: editUser?.preferences?.notification || (editUser?.role?.name === 'Call Center' ? ['phone', 'email'] : ['phone', 'email', 'sms']),
    role: editUser?.role?.name || 'Notification Contact',
  }

  const handleSubmit = async ({ firstName, lastName, email, mainNumber, mainNumberType, backupNumber, backupNumberType, preferences, role }: FormValues) => {
    const formattedMainNumber = await formatIntlPhoneNumber(mainNumber)
    const formattedBackupNumber = backupNumber ? await formatIntlPhoneNumber(backupNumber) : ''

    if (editUser) {
      const updatedUser = {
        id: editUser.id,
        information: {
          first: firstName,
          last: lastName,
        },
        email,
        phone: formattedMainNumber,
        metadata: {
          ...editUser.metadata,
          backup_phone: formattedBackupNumber,
          backup_phone_type: backupNumber ? backupNumberType : undefined,
          phone_type: mainNumberType,
          receiveAlerts: preferences.length > 0,
        },
        preferences: {
          ...editUser.preferences,
          notification: preferences,
        },
      }
      const res = await Api.put(`/api/v2/protect/folders/${selectedFolderId}/notification-contacts`, {
        user: updatedUser,
        role,
      })
      if (res.ok) {
        if (editingSelf) {
          setOwnUser((prev) =>
            prev
              ? {
                  ...prev,
                  information: { ...prev.information, ...updatedUser.information },
                  email,
                  phone: formattedMainNumber,
                  metadata: { ...prev.metadata, ...updatedUser.metadata },
                  preferences: { ...prev.preferences, ...updatedUser.preferences },
                }
              : prev
          )
        }
        enqueueSnackbar('Contact information updated')
        handleCloseContactForm()
        await reload?.()
      } else enqueueSnackbar('Unable to save changes', { variant: 'error' })
    } else {
      const newUser = {
        information: {
          first: firstName,
          last: lastName,
        },
        email,
        phone: formattedMainNumber,
        metadata: {
          backup_phone: formattedBackupNumber,
          backup_phone_type: backupNumber ? backupNumberType : undefined,
          phone_type: mainNumberType,
          phone_ext: '',
          receiveAlerts: preferences.length > 0,
        },
        preferences: {
          notification: preferences,
        },
      }

      const res = await Api.post(`/api/v2/protect/folders/${selectedFolderId}/notification-contacts`, {
        user: newUser,
        role,
        folderId: selectedFolderId,
      })
      if (res.ok) {
        if (allContacts && allContacts.some((contact) => contact.user.email === newUser.email)) {
          // edge case - if trying to create a contact that already exists, show better message
          enqueueSnackbar('The user you are trying to add to this location already exists and has been updated')
        } else {
          enqueueSnackbar('New contact added')
        }
        handleCloseContactForm()
        await reload?.()
      } else if (res?.body?.details?.[0]?.value?.signature === 'protect_permission_denied_folders_not_allowed')
        enqueueSnackbar('The user you are attempting to add to this location currently exists. Please contact Sensor Support.', { variant: 'error' })
      else enqueueSnackbar('Unable to add contact', { variant: 'error' })
    }
  }

  return (
    <Formik
      innerRef={formikRef}
      enableReinitialize={true}
      validateOnMount={true}
      initialValues={initialValues}
      onSubmit={handleSubmit}
      validationSchema={schema}
    >
      {({ isValid, isSubmitting, submitForm, values, setFieldValue, validateField, dirty }: FormikProps<FormValues>) => {
        isDirtyRef.current = dirty

        const handleRoleChange = async (role: FormValues['role']) => {
          await setFieldValue('role', role)

          if (values.mainNumber?.trim()) {
            // Revalidate main number so validation runs against the new role requirements.
            alreadyValidatedMainNumber = ''
            currentlyValidatingMainNumber = undefined
            await validateField('mainNumber')
          }
        }

        return (
          <FullScreenModal
            open={openContactForm}
            onClose={handleClose}
            showCloseIcon={true}
            title={editUser ? 'Edit Contact' : 'Add Contact'}
            maxWidth="sm"
            confirmButton={
              <Button
                type="submit"
                loading={isSubmitting}
                disabled={!isValid || isSubmitting || isEnteringMainNumber || isEnteringBackupNumber || !dirty}
                onClick={submitForm}
                variant="contained"
                disableElevation
                sx={contactDialogStyles.saveButton}
              >
                Save
              </Button>
            }
          >
            <Box sx={classes.contactFormContainer}>
              <Form style={{ width: '100%' }}>
                {!editUser && (
                  <Typography variant="body2" sx={contactDialogStyles.selectTypeText}>
                    Select Type
                  </Typography>
                )}

                <Grid container={true} size={12} sx={contactDialogStyles.roleTypeContainer}>
                  {!editUser && (
                    <ButtonGroup fullWidth={true}>
                      <ButtonGroup.Button
                        selected={values.role === 'Notification Contact'}
                        icon={values.role === 'Notification Contact' ? <CheckOutlined fontSize="small" /> : undefined}
                        onClick={() => handleRoleChange('Notification Contact')}
                      >
                        Person
                      </ButtonGroup.Button>
                      <ButtonGroup.Button
                        selected={values.role === 'Call Center'}
                        icon={values.role === 'Call Center' ? <CheckOutlined fontSize="small" /> : undefined}
                        onClick={() => handleRoleChange('Call Center')}
                      >
                        Call Center
                      </ButtonGroup.Button>
                    </ButtonGroup>
                  )}

                  {!editUser && values.role === 'Call Center' && (
                    <Grid container={true} size={12} sx={contactDialogStyles.callCenterDefinitionContainer}>
                      <Avatar sx={contactDialogStyles.callCenterDefinitionAvatar}>
                        <PersonOutlined color="primary" />
                      </Avatar>
                      <Grid container={true} sx={contactDialogStyles.callCenterDefinitionTextContainer}>
                        <Typography variant="body1">Call Center definition</Typography>
                        <Typography variant="body2" color="textSecondary">
                          Dispatchers coordinate incoming calls and assign them to the appropriate agents or technicians for resolution.
                        </Typography>
                      </Grid>
                    </Grid>
                  )}

                  <Grid container={true} size={12} sx={contactDialogStyles.contactInfoContainer}>
                    <Typography variant="body2" sx={contactDialogStyles.contactInfoText}>
                      Contact Information
                    </Typography>

                    <Grid container={true} size={12} sx={contactDialogStyles.nameFieldsContainer}>
                      <Grid size={values.role === 'Call Center' ? 12 : 6}>
                        <FormikTextField
                          name="firstName"
                          label={values.role === 'Call Center' ? 'Call Center' : 'First'}
                          variant="filled"
                          margin="dense"
                          sx={sharedClasses.input}
                        />
                        <FormHelperText />
                      </Grid>

                      {values.role !== 'Call Center' && (
                        <Grid size={6}>
                          <FormikTextField name="lastName" label="Last" variant="filled" margin="dense" sx={sharedClasses.input} />
                          <FormHelperText />
                        </Grid>
                      )}
                    </Grid>

                    <FormikTextField
                      name="email"
                      label="Email"
                      variant="filled"
                      startAdornment={<EmailOutlined fontSize="medium" color="secondary" />}
                      required={true}
                      sx={sharedClasses.input}
                      className="fs-exclude"
                    />

                    <Grid>
                      <FormikTextField
                        name="mainNumber"
                        label={values.role === 'Call Center' ? 'Landline' : 'Mobile Phone'}
                        startAdornment={
                          values.role === 'Call Center' ? (
                            <PhoneOutlined fontSize="medium" color="secondary" />
                          ) : (
                            <MobileFriendly fontSize="medium" color="secondary" />
                          )
                        }
                        required
                        onChange={() => {
                          isEnteringMainNumber = true
                        }}
                        onBlur={() => {
                          isEnteringMainNumber = false
                        }}
                        sx={sharedClasses.input}
                        variant="filled"
                      />
                    </Grid>

                    {showBackupPhone ? (
                      <Grid container={true} size={12} sx={contactDialogStyles.backupPhoneContainer}>
                        <FormikTextField
                          name="backupNumber"
                          label="Backup Phone"
                          startAdornment={
                            values.backupNumberType === 'Landline' ? (
                              <PhoneOutlined fontSize="medium" color="secondary" />
                            ) : (
                              <MobileFriendly fontSize="medium" color="secondary" />
                            )
                          }
                          onChange={() => {
                            isEnteringBackupNumber = true
                          }}
                          onBlur={async () => {
                            isEnteringBackupNumber = false
                            if (!values.backupNumber) return
                            // Call the phoneinfo endpoint and set backup number type based on response
                            const phoneNumber = (values.backupNumber as string).replace(/-|\s/g, '') // Remove spaces and hyphens to avoid the endpoint's max character limit of 15
                            const resPhoneInfo = await Api.get(`/api/users/phoneinfo?number=${phoneNumber}`)
                            if (resPhoneInfo.ok && !resPhoneInfo.body.carrier.error_code) {
                              const type = resPhoneInfo.body.carrier.type === 'mobile' ? 'Mobile Phone' : 'Landline'
                              setFieldValue('backupNumberType', type)
                            }
                          }}
                          sx={sharedClasses.input}
                          variant="filled"
                        />
                      </Grid>
                    ) : (
                      <Button
                        variant="text"
                        startIcon={<AddCircleOutlineOutlined />}
                        onClick={() => setShowBackupPhone(true)}
                        sx={contactDialogStyles.addBackupNumberButton}
                      >
                        Add backup number
                      </Button>
                    )}
                  </Grid>

                  <Grid container={true} sx={contactDialogStyles.alertPreferencesContainer}>
                    <Typography variant="body2" sx={contactDialogStyles.alertPreferencesText}>
                      During an urgent alert:
                    </Typography>

                    <ButtonGroup fullWidth={true}>
                      <ButtonGroup.Button
                        selected={values.preferences.includes('phone')}
                        icon={values.preferences.includes('phone') ? <CheckOutlined fontSize="small" /> : undefined}
                        onClick={() =>
                          setFieldValue(
                            'preferences',
                            values.preferences.includes('phone') ? values.preferences.filter((pref) => pref !== 'phone') : [...values.preferences, 'phone']
                          )
                        }
                        colorVariant="secondary"
                      >
                        Call
                      </ButtonGroup.Button>
                      <ButtonGroup.Button
                        selected={values.preferences.includes('email')}
                        icon={values.preferences.includes('email') ? <CheckOutlined fontSize="small" /> : undefined}
                        onClick={() =>
                          setFieldValue(
                            'preferences',
                            values.preferences.includes('email') ? values.preferences.filter((pref) => pref !== 'email') : [...values.preferences, 'email']
                          )
                        }
                        colorVariant="secondary"
                      >
                        Email
                      </ButtonGroup.Button>
                      <ButtonGroup.Button
                        selected={values.preferences.includes('sms')}
                        icon={values.preferences.includes('sms') ? <CheckOutlined fontSize="small" /> : undefined}
                        onClick={() =>
                          setFieldValue(
                            'preferences',
                            values.preferences.includes('sms') ? values.preferences.filter((pref) => pref !== 'sms') : [...values.preferences, 'sms']
                          )
                        }
                        colorVariant="secondary"
                      >
                        SMS
                      </ButtonGroup.Button>
                    </ButtonGroup>
                  </Grid>
                </Grid>
              </Form>
            </Box>

            <BasicDialog
              open={showDiscardDialog}
              onClose={() => setShowDiscardDialog(false)}
              textContent="You will lose your changes if you leave."
              onConfirm={handleDiscardChanges}
              confirmLabel="OK"
              closeLabel="Keep Editing"
            />
          </FullScreenModal>
        )
      }}
    </Formik>
  )
}

export default ContactDialog
